import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useGoalProgress } from "@/hooks/useGoalProgress";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, TrendingUp } from "lucide-react";
import { ComparisonBadge } from "./ComparisonBadge";

interface GoalProgressCardsProps {
  startDate: string;
  endDate: string;
}

export const GoalProgressCards = ({ startDate, endDate }: GoalProgressCardsProps) => {
  const { data: goals, isLoading } = useGoalProgress({ startDate, endDate });

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!goals || goals.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Ziel-Fortschritt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Keine Ziele definiert</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {goals.map((goal) => (
        <Card key={goal.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                {goal.name}
              </span>
              {goal.change !== undefined && goal.change !== 0 && (
                <ComparisonBadge value={goal.change} />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold">
                {goal.current_value?.toLocaleString() || 0}
              </span>
              <span className="text-sm text-muted-foreground">
                / {goal.target_value?.toLocaleString() || 0}
              </span>
            </div>
            <Progress 
              value={goal.progress || 0} 
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              {goal.progress || 0}% erreicht
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
